import { GeoJsonProperties, LineString, Position } from "geojson";
import { HlaWayType, hlaWayTypeIsRoad } from "../../../../hla/models/HlaWay";
import { categorizeCompositeFeature } from "../../../utils/query/categorizeCompositeFeature";
import turf from "../../../../shared/utils/turf";
import { delay } from "@keita/lanepatrol-sdk";
import { findAndRegisterOsmData } from "./findAndRegisterOsmData";
import { HlaWayBuilder } from "../models/HlaWayBuilder";
import { findAndRegisterAdjacentFeatures } from "../../../utils/query/findAndRegisterAdjacentFeatures";
import { importHlaUtils } from "../utils";
import { hlaWayLineLayerName } from "../../HlaMapSource";

export async function createWayFromLineFeature(
  featureId: number,
  coordinates: Position[],
  properties: GeoJsonProperties,
  enabledCategories: HlaWayType[],
  map: mapboxgl.Map,
  rightHandTraffic: boolean,
) {
  const props = properties!;
  const type = props.type ?? "";
  const className = props.class ?? "";

  var categoryName = categorizeCompositeFeature(className, type);

  if (!enabledCategories.includes(categoryName)) return;

  if (findExistingHlaWays(coordinates, map)) {
    console.log("Ignored way because a matching HLA way already exists.");
    return;
  }

  const lineString: LineString = {
    type: "LineString",
    coordinates,
  };

  console.log("Waiting and moving...");
  const featureCenter = turf.centroid(lineString);
  map.jumpTo({
    center: featureCenter.geometry.coordinates as mapboxgl.LngLatLike,
    zoom: 16,
  });
  await delay(200);

  const builder = new HlaWayBuilder(
    props.name,
    categoryName,
    coordinates,
    rightHandTraffic,
  );

  console.log("Importing", categoryName, props);

  builder.oneWay = props.oneway === "true";

  if (!!props.bike_lane && props.bike_lane !== "no") {
    if (props.bike_lane !== "left") {
      builder.right.cycleLane = "lane";
    }

    if (props.bike_lane !== "right") {
      builder.left.cycleLane = "lane";
    }
  }

  if (hlaWayTypeIsRoad(categoryName)) {
    builder.nRoadLanes = builder.oneWay ? 1 : 2;
  }

  findAndRegisterAdjacentFeatures(featureId, lineString, map, builder);

  findAndRegisterOsmData(builder, map);

  const way = builder.toCreateHlaWay();

  return way;
}

function findExistingHlaWays(coordinates: Position[], map: mapboxgl.Map) {
  const lineString = turf.lineString(coordinates);
  const bufferPolygon = turf.buffer(lineString, 1, {
    units: "meters",
  })!;
  const bbox = importHlaUtils.polygonToBbox(bufferPolygon, map);

  const hlaWayFeatures = importHlaUtils
    .flattenLineAndMultiLineFeatures(
      map.queryRenderedFeatures(bbox, {
        layers: [hlaWayLineLayerName],
      }),
    )
    .filter((feature) => turf.booleanIntersects(feature, bufferPolygon))
    .filter((f) => {
      const matches = f.geometry.coordinates.filter((position) => {
        const distances = turf.nearestPointOnLine(lineString, position, {
          units: "meters",
        });
        return distances.properties.pointDistance < 1;
      });

      return matches.length >= 2;
    });

  return hlaWayFeatures.length > 0;
}
